// eslint-disable-next-line import/no-extraneous-dependencies
import { ToastContainer } from 'react-toastify';
import './NotificationContainer.module.scss';
import type { ComponentChildren } from 'preact';
import useTranslations from '@/widgets/hooks/useTranslations';
import type { Translations } from '@/domain/WidgetConfig';
import useRoute from '@/widgets/hooks/useRoute';
import { isRTL } from '@/utils/isRTL';

/**
 * NotificationContainer component that provides a wrapper for displaying toast notifications
 * using the `react-toastify` library. This component is pre-configured with default options
 * for displaying notifications.
 *
 * @remarks
 * - Use notify fn of Notify/Notify.tsx to trigger notifications.
 * - Specific notification types can be triggered using `notify.warn`, `notify.error`,
 *   `notify.info`, or `notify.success`.
 *
 * @returns {JSX.Element} A `ToastContainer` component preconfigured for displaying notifications.
 *
 * @example
 * ```tsx
 * import { notify } from '../Notify/Notify.tsx';
 *
 * // Trigger a warning notification
 * notify('warning', 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.')
 *
 */
export default function NotificationContainer(): ComponentChildren {
  // based on figma https://www.figma.com/design/BECQGPto4Fdd1gR5TE0F72/Core-Components--Web-?node-id=281-20839&m=dev
  const { t } = useTranslations<Translations>();
  const { locale } = useRoute();

  return (
    <ToastContainer
      position="top-right"
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick={false}
      rtl={isRTL(locale)}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="light"
      aria-label={t('common.notifications.notifications')}
    />
  );
}
